import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { authApi } from '@/api/auth';
import { filialesApi, type MaSociete } from '@/api/filiales';
import { extractError } from '@/api/client';
import { oublierDonnees } from '@/lib/queryClient';
import type { Filiale, User } from '@/types';

interface AuthState {
  accessToken:    string | null;
  refreshToken:   string | null;
  user:           User | null;
  filiale:        Filiale | null;
  challengeToken: string | null;       // intermédiaire pendant le flow 2FA
  isLoading:      boolean;
  error:          { code: string; message: string } | null;

  // CTO-ACCESS-001 — mode "PDG vue filiale" (admin_holding lecture seule)
  pdgViewFiliale:        string | null;     // filialeKey en cours d'inspection (null = vue normale)
  pdgViewFilialeName:    string | null;     // nom human pour affichage bandeau
  pdgViewOriginalFiliale: Filiale | null;   // backup de la filiale Holding pour pouvoir revenir
  // Toggle écriture (ticket 14/09/2026) — défaut false = lecture seule
  pdgViewWrite:          boolean;

  // Lot 1, étape 1.7 — sélecteur de société
  societes:              MaSociete[] | null;  // sociétés rattachées (null = pas encore chargées)
  societeChoisie:        boolean;             // choix fait depuis la connexion (écran de choix passé)

  // Actions
  login:         (email: string, password: string) => Promise<{ requires2FA: boolean }>;
  verify2FA:     (code: string) => Promise<void>;
  refresh:       () => Promise<boolean>;
  loadFiliale:   () => Promise<void>;
  logout:        () => Promise<void>;
  clearError:    () => void;

  // PDG view actions
  enterPDGView:  (filialeKey: string, filialeName: string) => Promise<void>;
  exitPDGView:   () => Promise<void>;
  setPdgViewWrite: (enabled: boolean) => void;
  loadSocietes:   () => Promise<void>;
  /** Ouvre une société rattachée : la sienne = vue normale ; une autre = vue « lecture seule + Écriture » (D1.6-c). */
  changerSociete: (filialeKey: string) => Promise<void>;
}

/**
 * Persistance session (ticket SIMGROUP-2026-002 B-02/B-03) :
 *   Les tokens et user sont persistés en localStorage pour survivre au F5
 *   et permettre l'accès direct par URL. Compromis assumé : XSS pourrait
 *   voler le token (mitigation : CSP stricte + refresh token rotation
 *   backend + expiration courte). Avant : tokens en mémoire pure ->
 *   déconnexion à chaque reload -> désaffection utilisateurs (11/12 non
 *   connectés du 01/06 au 13/09/2026).
 *
 *   Migration future : cookie httpOnly Secure SameSite=Strict porté par
 *   backend -> plus safe mais nécessite changement API auth.
 */
/**
 * TICKET-004 C-05 (31/10/2026, « jusqu'à fermeture ») : le mode Écriture de la vue PDG survit aux rechargements de
 * l'ONGLET (sessionStorage, propre à l'onglet et effacé à sa fermeture), pour la seule société sur laquelle il a été
 * activé ; il ne passe jamais à un autre onglet ni à une autre société (règle de sécurité du 14/09 conservée).
 */
const CLE_ECRITURE = 'simgroup.ecriture';
const memoriserEcriture = (filiale: string | null) => {
  try { if (filiale) sessionStorage.setItem(CLE_ECRITURE, filiale); else sessionStorage.removeItem(CLE_ECRITURE); } catch { /* sans effet */ }
};
const ecritureMemorisee = (): string | null => { try { return sessionStorage.getItem(CLE_ECRITURE); } catch { return null; } };

export const useAuthStore = create<AuthState>()(persist((set, get) => ({
  accessToken:    null,
  refreshToken:   null,
  user:           null,
  filiale:        null,
  challengeToken: null,
  isLoading:      false,
  error:          null,
  pdgViewFiliale:         null,
  pdgViewFilialeName:     null,
  pdgViewOriginalFiliale: null,
  pdgViewWrite:           false,
  societes:               null,
  societeChoisie:         false,

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const resp = await authApi.login({ email, password });
      if (resp.requires2FA && resp.challengeToken) {
        set({ challengeToken: resp.challengeToken, isLoading: false });
        return { requires2FA: true };
      }
      if (resp.accessToken && resp.refreshToken && resp.user) {
        set({
          accessToken:  resp.accessToken,
          refreshToken: resp.refreshToken,
          user:         resp.user,
          isLoading:    false,
          societes:     null,
          societeChoisie: false,
        });
        // Charger la filiale en parallèle, sans bloquer le login
        get().loadFiliale().catch(() => {/* déjà géré */});
        get().loadSocietes().catch(() => {/* sélecteur absent, sans effet sur le reste */});
        return { requires2FA: false };
      }
      throw new Error('Réponse de login inattendue');
    } catch (err) {
      set({ error: extractError(err), isLoading: false });
      throw err;
    }
  },

  verify2FA: async (code) => {
    const challengeToken = get().challengeToken;
    if (!challengeToken) {
      throw new Error('Aucun challenge 2FA en cours');
    }
    set({ isLoading: true, error: null });
    try {
      const resp = await authApi.loginWith2fa({ challengeToken, code });
      if (resp.accessToken && resp.refreshToken && resp.user) {
        set({
          accessToken:    resp.accessToken,
          refreshToken:   resp.refreshToken,
          user:           resp.user,
          challengeToken: null,
          isLoading:      false,
          societes:       null,
          societeChoisie: false,
        });
        get().loadFiliale().catch(() => {});
        get().loadSocietes().catch(() => {});
        return;
      }
      throw new Error('Réponse 2FA inattendue');
    } catch (err) {
      set({ error: extractError(err), isLoading: false });
      throw err;
    }
  },

  refresh: async () => {
    const rt = get().refreshToken;
    if (!rt) return false;
    try {
      const resp = await authApi.refresh({ refreshToken: rt });
      set({ accessToken: resp.accessToken, refreshToken: resp.refreshToken });
      return true;
    } catch {
      return false;
    }
  },

  loadFiliale: async () => {
    try {
      const filiale = await filialesApi.getMe();
      if (!filiale || !filiale.filialeKey) {
        // JWT stale (hook custom_access_token_hook n'a pas posé filiale_code dans les claims,
        // ou user supprimé entre temps). Re-login obligatoire pour récupérer un JWT propre.
        await get().logout();
        return;
      }
      set({ filiale });
    } catch (err) {
      const e = extractError(err);
      set({ error: e });
      // 401/403/404 sur /filiales/me = JWT inutilisable. Forcer logout pour éviter sidebar vide.
      if (e.code === 'FILIALE_NOT_FOUND' || e.code === 'UNAUTHORIZED' || e.code === 'FORBIDDEN') {
        await get().logout();
      }
    }
  },

  logout: async () => {
    memoriserEcriture(null);
    await authApi.logout();
    oublierDonnees(); // le compte suivant ne doit rien revoir du précédent
    set({
      accessToken:    null,
      refreshToken:   null,
      user:           null,
      filiale:        null,
      challengeToken: null,
      error:          null,
      pdgViewFiliale:         null,
      pdgViewFilialeName:     null,
      pdgViewOriginalFiliale: null,
      pdgViewWrite:           false,
      societes:               null,
      societeChoisie:         false,
    });
  },

  clearError: () => set({ error: null }),

  /**
   * Entre en mode "PDG View Filiale" (CTO-ACCESS-001). Le PDG (admin_holding)
   * inspecte une filiale en lecture seule. Tous les fetch API auront le header
   * `X-PDG-View-Filiale` ajouté automatiquement. Backend force read-only.
   * Recharge ensuite la filiale (avec le header désormais actif) → thème + metier
   * complets depuis l'API /filiales/me du tenant cible.
   */
  enterPDGView: async (filialeKey, filialeName) => {
    const currentFiliale = get().filiale;
    set({
      pdgViewFiliale:         filialeKey,
      pdgViewFilialeName:     filialeName,
      pdgViewOriginalFiliale: currentFiliale,
      pdgViewWrite:           false,  // toujours démarrer en lecture seule
    });
    memoriserEcriture(null);
    oublierDonnees(); // aucune donnée de la société précédente ne doit resservir
    // Recharge la filiale cible (avec header X-PDG-View-Filiale désormais actif)
    await get().loadFiliale();
  },

  /** Sort du mode PDG View, retour au scope Holding original. */
  exitPDGView: async () => {
    memoriserEcriture(null);
    const original = get().pdgViewOriginalFiliale;
    set({
      pdgViewFiliale:         null,
      pdgViewFilialeName:     null,
      pdgViewOriginalFiliale: null,
      pdgViewWrite:           false,
      filiale:                original ?? get().filiale,
    });
    oublierDonnees();
    // Recharge la filiale du contexte normal (sans header) pour récupérer la vraie filiale d'origine
    if (!original) await get().loadFiliale();
  },

  /**
   * Active/désactive l'écriture en mode PDG View (ticket 14/09/2026).
   * Défaut à false pour éviter modifs accidentelles. Le PDG doit basculer
   * volontairement via le toggle du bandeau orange pour éditer une filiale.
   */
  setPdgViewWrite: (enabled) => {
    set({ pdgViewWrite: enabled });
    memoriserEcriture(enabled ? get().pdgViewFiliale : null);
  },

  loadSocietes: async () => {
    const societes = await filialesApi.mesSocietes();
    set({ societes });
  },

  changerSociete: async (filialeKey) => {
    const cible = get().societes?.find((s) => s.filialeKey === filialeKey);
    if (!cible || !cible.ouvrable) return;
    // Toujours repartir de sa société d'origine, puis entrer dans la cible : sinon la « société d'origine »
    // mémorisée serait la société précédemment ouverte.
    if (get().pdgViewFiliale) await get().exitPDGView();
    if (!cible.origine) await get().enterPDGView(cible.filialeKey, cible.name);
    set({ societeChoisie: true });
    try { localStorage.setItem(`simgroup.derniereSociete.${get().user?.id ?? ''}`, filialeKey); } catch { /* sans effet */ }
  },
}), {
  name: 'simgroup-auth',
  storage: createJSONStorage(() => localStorage),
  // Ne persiste QUE ce qui est nécessaire à la reprise de session.
  // - challengeToken/isLoading/error : ephémères, jamais persistés
  // - pdgViewWrite : toujours reset à false au reload (safety : jamais
  //   d'écriture PDG persistante entre onglets ni entre sessions)
  partialize: (state) => ({
    accessToken:            state.accessToken,
    refreshToken:           state.refreshToken,
    user:                   state.user,
    filiale:                state.filiale,
    pdgViewFiliale:         state.pdgViewFiliale,
    pdgViewFilialeName:     state.pdgViewFilialeName,
    pdgViewOriginalFiliale: state.pdgViewOriginalFiliale,
    societes:               state.societes,
    societeChoisie:         state.societeChoisie,
  }),
  // Au rehydrate : recharge filiale en tâche de fond pour rafraîchir thème/metier
  // (le user peut avoir été modifié côté serveur pendant que la session dormait).
  onRehydrateStorage: () => (state) => {
    // C-05 : écriture réactivée après un rechargement de cet onglet, sur la même société seulement.
    if (state?.pdgViewFiliale && ecritureMemorisee() === state.pdgViewFiliale) {
      Promise.resolve().then(() => useAuthStore.setState({ pdgViewWrite: true }));
    }
    if (state?.accessToken && state?.user) {
      // Fire-and-forget : ne bloque pas le premier rendu. Si le refresh échoue
      // (token expiré côté serveur), le prochain 401 déclenchera le logout.
      Promise.resolve().then(() => state.loadFiliale?.().catch(() => {/* déjà géré */}));
      // Lot 1, étape 1.7 : la liste des sociétés est relue à chaque ouverture (rattachements modifiés entre-temps).
      Promise.resolve().then(() => state.loadSocietes?.().catch(() => {/* sélecteur absent, sans effet */}));
    }
  },
}));
